import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable, isDevMode } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    NotificationErrorDialogComponent,
    SentinelNotification,
    SentinelNotificationResult,
    SentinelNotificationService,
    SentinelNotificationTypeEnum
} from '@sentinel/layout/notification';
import { Observable, of, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { PortalConfig } from '../../types/config';
import { Event, NavigationError, Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
/**
 * Resolves type of error and emits alert with appropriate message
 */
export class ErrorHandlerService implements ErrorHandler {
    private config = inject(PortalConfig);
    private notificationService = inject(SentinelNotificationService);
    private dialog = inject(MatDialog);
    private router = inject(Router);

    private navigationErrorSubject = new Subject<NavigationError>();
    public navigationError$ = this.navigationErrorSubject.asObservable();

    constructor() {
        this.setupNavigationErrorHandling();
    }

    handleError(error: any) {
        console.error('An error occurred:', error);
    }

    emitNavigationError(error: any, url?: string): Observable<boolean> {
        if (!isDevMode()) {
            console.warn('Unable to navigate to requested page:', url, error);
            return of(true);
        }
        const notification: SentinelNotification = {
            type: SentinelNotificationTypeEnum.Error,
            title: 'Navigation',
            source: url || 'Unknown url',
            additionalInfo: [
                'Unable to navigate to the requested page.',
                error?.message || 'Unknown navigation error',
            ],
        };

        return this.displayWithoutStoring(notification);
    }

    emitAPIError(
        err: HttpErrorResponse,
        operation: string,
    ): Observable<boolean> {
        console.error(err);
        const notification: SentinelNotification = {
            type: SentinelNotificationTypeEnum.Error,
            title: operation,
        };

        if (
            err === null ||
            err === undefined ||
            err.status === 0 ||
            err.error === null ||
            err.error === undefined
        ) {
            const msg = err?.message || '';
            notification.additionalInfo = [
                'Unknown error. Please check your internet connection or report the issue to developers',
                msg.toString(),
            ];
            this.checkForClockSyncErr(err, notification);
            return this.safeEmit(
                this.notificationService,
                'emit',
                notification,
            );
        }

        if (
            err.url?.startsWith(this.config.basePaths.linearTraining)
        ) {
            this.setJavaApiErrorNotification(err, notification);
            notification.source = 'Training Agenda';
        } else if (err.url?.startsWith(this.config.basePaths.userAndGroup)) {
            this.setJavaApiErrorNotification(err, notification);
            notification.source = 'User & Group Agenda';
        } else if (err.url?.startsWith(this.config.basePaths.sandbox)) {
            this.setPythonApiErrorToNotification(err, notification);
            notification.source = 'Sandbox Agenda';
        } else {
            notification.additionalInfo = [
                `The request failed with HTTP ${err.status}. Report this to the platform developers.`,
            ];
        }

        return this.safeEmit(this.notificationService, 'emit', notification);
    }

    emitFrontendErrorNotification(
        error: string,
        source?: string,
    ): Observable<boolean> {
        return this.safeEmit(this.notificationService, 'emit', {
            type: SentinelNotificationTypeEnum.Error,
            title: source || 'Error',
            additionalInfo: this.toNotificationLines(error),
        });
    }

    /**
     * Safely invokes a method that should return Observable<SentinelNotificationResult>.
     * Falls back to `of(false)` if the method is missing or throws.
     */
    private safeEmit(
        service: any,
        method: string,
        payload: SentinelNotification,
    ): Observable<boolean> {
        if (typeof service?.[method] !== 'function') {
            return of(false);
        }
        try {
            return service[method](payload).pipe(
                map((r: any) => r === SentinelNotificationResult.CONFIRMED),
            );
        } catch {
            return of(false);
        }
    }

    /**
     * Shows an error notification in the Sentinel error dialog without adding it to the
     * notification history. Skipped while another dialog is open.
     *
     * @returns Whether the user confirmed the notification's action.
     */
    private displayWithoutStoring(
        notification: SentinelNotification,
    ): Observable<boolean> {
        if (this.dialog.openDialogs.length > 0) {
            return of(false);
        }
        return this.dialog
            .open(NotificationErrorDialogComponent, {
                data: notification,
                disableClose: true,
            })
            .afterClosed()
            .pipe(
                map((result) => result === SentinelNotificationResult.CONFIRMED),
            );
    }

    private setupNavigationErrorHandling(): void {
        this.router.events
            .pipe(
                filter(
                    (event: Event): event is NavigationError =>
                        event instanceof NavigationError,
                ),
            )
            .subscribe((errorEvent: NavigationError) => {
                this.navigationErrorSubject.next(errorEvent);
                this.emitNavigationError(errorEvent.error, errorEvent.url);
            });
    }

    private setJavaApiErrorNotification(
        err: HttpErrorResponse,
        notification: SentinelNotification,
    ) {
        notification.additionalInfo = this.toNotificationLines(
            this.describedCause(err),
        );
    }

    /**
     * Picks the most telling text a failed response carries, preferring the message its body
     * states, then the detail (falling back to the title) of an RFC 9457 problem body, then a body
     * that is itself text, and settling for the transport-level message when the body holds none.
     * Keeps a body of an unforeseen shape from yielding a blank line.
     */
    private describedCause(err: HttpErrorResponse): string {
        const statedMessage = err.error?.message;
        if (typeof statedMessage === 'string' && statedMessage.length > 0) {
            return statedMessage;
        }
        for (const problemText of [err.error?.detail, err.error?.title]) {
            if (typeof problemText === 'string' && problemText.length > 0) {
                return problemText;
            }
        }
        if (typeof err.error === 'string' && err.error.length > 0) {
            return err.error;
        }
        return err.message;
    }

    /**
     * Splits a message into one entry per line, dropping blank ones, so a message composed of
     * several statements is offered to the notification as several lines instead of one block.
     * A message holding no line break, and one that is not text at all, yields a single entry.
     */
    private toNotificationLines(message: unknown): string[] {
        if (typeof message !== 'string') {
            return [String(message ?? '')];
        }
        const lines = message
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line.length > 0);
        return lines.length > 0 ? lines : [message];
    }

    private setPythonApiErrorToNotification(
        err: HttpErrorResponse,
        notification: SentinelNotification,
    ) {
        if (err.error.detail) {
            notification.additionalInfo = [err.error.detail];
        } else if (err.error.non_field_errors) {
            notification.additionalInfo = [err.error.non_field_errors];
        }
    }

    private checkForClockSyncErr(
        err: HttpErrorResponse | string,
        notification: SentinelNotification,
    ) {
        if (err === 'Token has expired') {
            notification.source = err;
            notification.additionalInfo = [
                'Failed due to an expired authentication. Please, make sure your local date and time is correct.',
            ];
        }
    }
}
